import styled from "styled-components";
import * as ICONS from "@bracket/ui/icons";
import { getTheme, themed, isThemeColor, Box, type ColorOption, type IconName, } from "@bracket/ui";
import { Fragment, type FunctionComponent, } from "react";


export declare type IconProps = {
	icon: IconName;
	className?: string;
	size?: string;
	height?: string;
	width?: string;
	scale?: string | number;
	rotation?: number;
	/** explicit icon color; overrides theme default */
	color?: ColorOption|"inherit";
	circle?: boolean;
	circlePad?: string;
	circleWidth?: string;
	circleColor?: string;
	style?: React.CSSProperties;
	ref?: React.RefObject<HTMLDivElement>;
};



/**
 * A standalone icon component that displays any icon from the Icons library.
 *
 * @component
 * @param {Object} props - Component props
 * @param {keyof typeof ICONS} props.icon - Icon name from ICONS to display
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.size="1.5rem"] - Size of the icon (default: "1.5rem")
 * @param {string | number} [props.scale] - CSS scale value to apply to the icon (e.g., "1.5" or 1.5)
 * @param {number} [props.rotation] - Rotation angle in degrees (1-360, will be clamped to this range)
 * @param {boolean} [props.circle] - Whether to draw a circle border around the icon
 * @param {string} [props.circlePad] - CSS size value for padding between the circle border and icon (e.g., "0.5rem" or "8px")
 * @param {string} [props.circleWidth] - CSS size value for the circle border width (default: "1px")
 * @param {string} [props.circleColor] - CSS color value for the circle border (default: "#fff")
 * @returns {JSX.Element} An icon component
 */
export const Icon: FunctionComponent<IconProps> = ({
	icon,
	className = "",
	size = "100%",
	height,
	width,
	scale,
	rotation,
	color,
	circle,
	circlePad,
	circleWidth = "1px",
	circleColor = "#fff",
	style,
	ref,
}) => {
	const IconComponent = ((icon && ICONS[icon]) || Fragment) as FunctionComponent;
	const clampedRotation = rotation !== undefined ? Math.max(1, Math.min(360, rotation)) : undefined;
	return (
		<StyledIcon
			ref={ref}
			style={style}
			$size={size}
			$width={width}
			$height={height} $scale={scale} $rotation={clampedRotation} $color={color} $circle={circle} $circlePad={circlePad} $circleWidth={circleWidth} $circleColor={circleColor} className={className}
		>
			<IconComponent />
		</StyledIcon>
	);
};



const StyledIcon = styled(Box)<{
	$size: string;
	$width?: string;
	$height?: string;
	$scale?: string | number;
	$rotation?: number;
	$color?: ColorOption;
	$circle?: boolean;
	$circlePad?: string;
	$circleWidth?: string;
	$circleColor?: string;
}>`
	display: flex;
	align-items: center;
	justify-content: center;
	transition: transform 0.2s ease;
	color: ${({ $color, }) => $color ? isThemeColor($color) ? getTheme()[$color] : $color : "inherit"};
	width: ${({ $width, }) => $width || ""};
	height: ${({ $height, }) => $height || ""};
	${({ $scale, $rotation, }) => {
		const transforms: string[] = [];
		if($scale !== undefined) {
			transforms.push(`scale(${$scale})`);
		}
		if($rotation !== undefined) {
			transforms.push(`rotate(${$rotation}deg)`);
		}
		return transforms.length > 0 ? `transform: ${transforms.join(" ")};` : "";
	}}
	${({ $circle, $size, $circlePad, $circleWidth, $circleColor, }) => {
		if(!$circle) {
			return "";
		}
		const borderWidth = $circleWidth || "1px";
		const borderColor = $circleColor || "#fff";
		const padding = $circlePad || "0px";
		// Use box-sizing to include border and padding in the total size
		return `
			border: ${borderWidth} solid ${borderColor};
			border-radius: 50%;
			padding: ${padding};
			box-sizing: border-box;
			width: calc(${$size} + ${borderWidth} + ${borderWidth} + ${padding} + ${padding});
			height: calc(${$size} + ${borderWidth} + ${borderWidth} + ${padding} + ${padding});
			min-width: calc(${$size} + ${borderWidth} + ${borderWidth} + ${padding} + ${padding});
			min-height: calc(${$size} + ${borderWidth} + ${borderWidth} + ${padding} + ${padding});
		`;
	}}

	svg {
		width: ${({ $size, }) => $size};
		height: ${({ $size, }) => $size};
	}
`;

